import type React from 'react'
import {
  getFormulaFunctionAssistance,
  type FormulaEditSession,
  type FormulaFunctionSignature,
} from '../spreadsheet/formulaAuthoring'

interface FormulaFunctionAssistantProps {
  session: FormulaEditSession | null | undefined
  text: string
  style?: React.CSSProperties
  nameSuggestions?: FormulaNameSuggestion[]
  activeNameSuggestionIndex?: number
  onSelectSuggestion?: (name: string) => void
  onSelectNameSuggestion?: (name: string) => void
}

export type FormulaNameSuggestion = {
  name: string
  scopeLabel?: string
  refersTo?: string
}

const renderSignature = (
  signature: FormulaFunctionSignature,
  activeArgumentIndex: number | null,
) => (
  <span>
    <strong>{signature.name}</strong>
    {'('}
    {signature.args.map((arg, index) => (
      <span key={`${signature.name}:${arg}:${index}`}>
        {index > 0 ? ', ' : ''}
        <span style={{ fontWeight: index === activeArgumentIndex ? 700 : 400 }}>
          {arg}
        </span>
      </span>
    ))}
    {')'}
  </span>
)

export default function FormulaFunctionAssistant({
  session,
  text,
  style,
  nameSuggestions = [],
  activeNameSuggestionIndex = 0,
  onSelectSuggestion,
  onSelectNameSuggestion,
}: FormulaFunctionAssistantProps) {
  if (!session || session.mode === 'value') return null
  const assistance = getFormulaFunctionAssistance(text, session.caretEnd, session.functionSuggestionIndex)
  const parseError = session.parseError
  const hasNameSuggestions = nameSuggestions.length > 0
  if (!assistance && !parseError && !hasNameSuggestions) return null
  if (assistance && !assistance.activeFunction && assistance.suggestions.length === 0 && !parseError && !hasNameSuggestions) return null

  const pointerEvents = session.mode === 'point' || session.mode === 'range-resize' ? 'none' : 'auto'
  const primary = assistance
    ? assistance.activeFunction ?? assistance.suggestions[assistance.activeSuggestionIndex]
    : null
  const detailRows = assistance?.activeArgumentDetails.length
    ? assistance.activeArgumentDetails
    : primary?.details ?? []

  return (
    <div
      className="fg-formula-function-assistant"
      style={{
        position: 'absolute',
        left: 0,
        top: 'calc(100% + 4px)',
        minWidth: 280,
        maxWidth: 520,
        padding: '8px 10px',
        border: '1px solid rgba(148,163,184,0.7)',
        borderRadius: 6,
        background: '#ffffff',
        color: '#111827',
        boxShadow: '0 12px 28px rgba(15,23,42,0.18)',
        fontSize: 12,
        lineHeight: '18px',
        pointerEvents,
        zIndex: 120,
        ...style,
      }}
      onMouseDown={(event) => {
        event.preventDefault()
      }}
    >
      {primary && assistance && (
        <>
          <div style={{ color: '#111827' }}>
            {renderSignature(primary, assistance.activeFunction ? assistance.activeArgumentIndex : null)}
          </div>
          <div style={{ color: '#4b5563', marginTop: 3 }}>
            {primary.description}
          </div>
        </>
      )}
      {detailRows.map((detail) => (
        <div key={detail} style={{ color: '#64748b', marginTop: 2 }}>
          {detail}
        </div>
      ))}
      {parseError && (
        <div
          data-formula-parse-error="true"
          style={{
            color: '#b91c1c',
            marginTop: primary ? 6 : 0,
            borderTop: primary ? '1px solid rgba(254,202,202,0.8)' : undefined,
            paddingTop: primary ? 6 : 0,
          }}
        >
          {parseError.message}
        </div>
      )}
      {assistance && !assistance.activeFunction && assistance.suggestions.length > 1 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {assistance.suggestions.slice(0, 6).map((suggestion, index) => (
            <button
              key={suggestion.name}
              type="button"
              aria-selected={index === assistance.activeSuggestionIndex}
              title={`Insert ${suggestion.name} function`}
              data-formula-function-suggestion={suggestion.name}
              data-formula-function-suggestion-active={index === assistance.activeSuggestionIndex ? 'true' : 'false'}
              onClick={() => onSelectSuggestion?.(suggestion.name)}
              style={{
                color: index === assistance.activeSuggestionIndex ? '#ffffff' : '#334155',
                border: index === assistance.activeSuggestionIndex ? '1px solid var(--fg-green)' : '1px solid rgba(203,213,225,0.85)',
                borderRadius: 4,
                backgroundColor: index === assistance.activeSuggestionIndex ? 'var(--fg-green)' : '#ffffff',
                padding: '1px 5px',
                font: 'inherit',
                cursor: onSelectSuggestion ? 'pointer' : 'default',
              }}
            >
              {suggestion.name}
            </button>
          ))}
        </div>
      )}
      {hasNameSuggestions && (
        <div
          data-formula-name-suggestions="true"
          style={{
            display: 'grid',
            gap: 4,
            marginTop: primary || detailRows.length || parseError ? 8 : 0,
            borderTop: primary || detailRows.length || parseError ? '1px solid rgba(226,232,240,0.9)' : undefined,
            paddingTop: primary || detailRows.length || parseError ? 8 : 0,
          }}
        >
          {nameSuggestions.slice(0, 8).map((suggestion, index) => (
            <button
              key={`${suggestion.scopeLabel ?? 'name'}:${suggestion.name}`}
              type="button"
              aria-selected={index === activeNameSuggestionIndex}
              title={suggestion.refersTo ? `${suggestion.name}: ${suggestion.refersTo}` : `Insert ${suggestion.name}`}
              data-formula-name-suggestion={suggestion.name}
              data-formula-name-suggestion-active={index === activeNameSuggestionIndex ? 'true' : 'false'}
              onClick={() => onSelectNameSuggestion?.(suggestion.name)}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                color: index === activeNameSuggestionIndex ? '#ffffff' : '#334155',
                border: index === activeNameSuggestionIndex ? '1px solid var(--fg-green)' : '1px solid rgba(203,213,225,0.85)',
                borderRadius: 4,
                backgroundColor: index === activeNameSuggestionIndex ? 'var(--fg-green)' : '#ffffff',
                padding: '3px 6px',
                font: 'inherit',
                textAlign: 'left',
                cursor: onSelectNameSuggestion ? 'pointer' : 'default',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 700 }}>
                {suggestion.name}
              </span>
              {suggestion.scopeLabel && (
                <small style={{ color: index === activeNameSuggestionIndex ? 'rgba(255,255,255,0.85)' : '#64748b' }}>
                  {suggestion.scopeLabel}
                </small>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
